import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plus,
  Edit3,
  Trash2,
  Search as SearchIcon,
  Package,
} from "lucide-react";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";
const API = `${BACKEND_URL}`;

export default function CategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id: "", name: "", icon: "" });
  // Lista de ícones populares para seleção rápida
  const iconOptions = [
    "🍔",
    "🍕",
    "🥗",
    "🍣",
    "🍜",
    "🍰",
    "🍺",
    "☕",
    "🥤",
    "🍩",
    "🍟",
    "🍛",
    "🍦",
    "🍇",
    "🍉",
    "🍊",
    "🍋",
    "🍌",
    "🍎",
    "🍏",
    "🍒",
    "🍓",
    "🥑",
    "🥕",
    "🌽",
    "🥒",
    "🍆",
    "🥚",
    "🥩",
    "🍗",
    "🍖",
    "🧀",
    "🍞",
    "🥨",
    "🍿",
    "🍫",
    "🍪",
    "🍬",
    "🍭",
    "🍯",
    "🥜",
    "🌰",
    "🍧",
    "🧃",
    "🎉",
    "❄️",
    "🎊",
    "🎈",
    "🎀",
    "✨",
    "⭐",
    "💎",
    "🔥",
    "🧊",
    "🍨",
    "🍮",
    "🥣",
    "🧁",
    "🔍",
  ];
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setFetching(true);
    try {
      const res = await axios.get(`${API}/categories`);
      const data = res?.data?.data ?? res?.data ?? [];
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error fetching categories:", e);
      setCategories([]);
    } finally {
      setFetching(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ id: "", name: "", icon: "" });
    setShowDialog(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ id: cat.id ?? "", name: cat.name ?? "", icon: cat.icon ?? "" });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name || form.name.trim().length === 0) {
      setFormError("Nome é obrigatório");
      return;
    }
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`${API}/categories/${editing.id}`, form);
      } else {
        await axios.post(`${API}/categories`, form);
      }
      await fetchCategories();
      setShowDialog(false);
    } catch (err) {
      console.error("Error saving category:", err);
      setFormError("Erro ao salvar categoria");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Excluir categoria "${cat.name}"?`)) return;
    try {
      await axios.delete(`${API}/categories/${cat.id}`);
      await fetchCategories();
    } catch (e) {
      console.error("Error deleting category:", e);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Modal de Loading */}
      <Dialog open={fetching} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#c9332C] rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Package className="h-6 w-6 text-[#c9332C]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              Carregando categorias...
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <h2 className="text-3xl font-bold text-gray-900">
            Gestão de Categorias
          </h2>
        </div>

        <div className="flex gap-3 items-center w-full md:w-auto">
          <div className="relative flex-1 md:flex-none">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar por nome ou id..."
              className="pl-10 w-full md:w-64"
            />
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                className=""
                style={{ background: "#c9332C" }}
              >
                <Plus className="h-4 w-4 mr-2" /> Nova Categoria
              </Button>
            </DialogTrigger>

            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Editar Categoria" : "Nova Categoria"}
                </DialogTitle>
                <DialogDescription>
                  {editing
                    ? "Atualize a categoria."
                    : "Adicione uma nova categoria."}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="id">Chave (id)</Label>
                  <Input
                    id="id"
                    value={form.id}
                    onChange={(e) => setForm({ ...form, id: e.target.value })}
                    placeholder="ex: bebida_gelada"
                  />
                </div>
                <div>
                  <Label htmlFor="icon">Ícone</Label>
                  <div className="flex gap-2 flex-wrap mb-2">
                    {iconOptions.map((ico) => (
                      <button
                        type="button"
                        key={ico}
                        className={`border rounded px-2 py-1 text-xl bg-white hover:bg-gray-100 focus:outline-none ${
                          form.icon === ico
                            ? "border-red-600"
                            : "border-gray-300"
                        }`}
                        onClick={() => setForm({ ...form, icon: ico })}
                        aria-label={`Selecionar ícone ${ico}`}
                      >
                        {ico}
                      </button>
                    ))}
                  </div>
                  <Input
                    id="icon"
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    placeholder="Emoji ou texto curto"
                  />
                </div>

                {formError && (
                  <div className="text-red-600 text-sm">{formError}</div>
                )}

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDialog(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {editing ? "Atualizar" : "Criar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(() => {
          const q = query.trim().toLowerCase();
          const list = q
            ? categories.filter((c) =>
                `${String(c.id || "").toLowerCase()} ${String(
                  c.name || ""
                ).toLowerCase()}`.includes(q)
              )
            : categories;

          if (!fetching && !list.length) {
            return (
              <div className="col-span-full text-center text-gray-500 py-10">
                {q
                  ? "Nenhuma categoria encontrada."
                  : "Nenhuma categoria cadastrada."}
              </div>
            );
          }

          return list.map((cat) => (
            <Card
              key={cat.id}
              className="overflow-hidden hover:shadow-lg transition-shadow"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">
                    {cat.icon} {cat.name}
                  </h3>
                  <span className="text-sm text-gray-500">ID: {cat.id}</span>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(cat)}
                  >
                    <Edit3 className="h-4 w-4 mr-1" /> Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(cat)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ));
        })()}
      </div>
    </div>
  );
}
